<template>
    <div :class="padding ? 'px-4' : ''" v-if="product">
        <NuxtLink
            class="product relative block"
            @click="addToState(product.id, requestId)"
            :to="productLink"
            :data-product="addToHistory ? product.id : null">
            <div
                class="group relative aspect-3/4 rounded-sm bg-[#F6F6F6] lg:mb-2"
                @mouseenter="handleMouseEnter"
                @mouseleave="handleMouseLeave"
                @focus="handleMouseEnter"
                @blur="handleMouseLeave">
                <picture v-if="product.cover" class="contents">
                    <source :srcset="activeImageSources.avif.srcset" type="image/avif" />
                    <source :srcset="activeImageSources.webp.srcset" type="image/webp" />
                    <img
                        class="img h-full w-full rounded-sm object-cover"
                        :alt="product.cover.media.alt || ''"
                        :src="activeImageSources.webp.src"
                        :srcset="activeImageSources.webp.srcset"
                        width="600"
                        height="800"
                        :loading="isAboveFold ? 'eager' : 'lazy'"
                        :fetchpriority="isAboveFold ? 'high' : 'auto'" />
                </picture>
                <div
                    v-else
                    :class="imageSize"
                    class="img my-[25px] flex h-[180px] w-full flex-col items-center justify-between rounded-sm bg-black p-12 text-white lg:p-20">
                    <p class="text-center leading-normal tracking-wider lg:text-2xl lg:leading-8">
                        {{ snippets['headless.product.placeholderNoImage'] }}
                    </p>
                    <div class="text-center">
                        <p class="text-xss leading-4 font-light tracking-wider lg:text-sm">BALLEGOOYEN</p>
                        <p class="text-xxs leading-3 font-light tracking-widest">modes</p>
                    </div>
                </div>
                <div v-if="props.showLabels" class="absolute bottom-3 left-0 flex flex-col space-y-1 md:space-y-2">
                    <span
                        v-if="productIsNew"
                        class="new-tag text-xxs lg:text-xss mb-0 w-max px-2.5! py-1! uppercase lg:px-4 lg:py-2!">
                        {{ snippets['headless.product.newLabel'] || 'Nieuw' }}
                    </span>
                    <span
                        v-if="productIsOnSale"
                        class="sale-tag text-xxs lg:text-xss mb-0 w-max px-2.5 py-[7px] uppercase lg:px-4 lg:py-2">
                        {{ saleLabel }}
                    </span>
                    <template v-if="product?.extensions?.categoryLabels?.length > 0">
                        <span
                            v-for="label in product?.extensions?.categoryLabels"
                            :key="label?.id"
                            class="font-monument text-xxs lg:text-xss mb-0 w-max px-1.5 py-1 leading-[14px] font-bold tracking-[1.25px] lg:px-4 lg:py-2"
                            :style="{ backgroundColor: label?.backgroundColor, color: label?.color }">
                            {{ label?.name }}
                        </span>
                    </template>
                </div>
            </div>
            <p v-if="product.manufacturer.translated.name" class="mt-2 leading-5 lg:text-sm">
                {{ product.manufacturer.translated.name }}
            </p>
            <p v-if="product.translated.name" class="leading-5 lg:text-sm">
                {{ product.translated.name }}
            </p>
            <p
                v-if="product.calculatedPrice.totalPrice"
                class="text-sm leading-6 font-bold tracking-[0.7px] lg:text-base">
                <span
                    :class="{
                        '!text-sm line-through': productIsOnSale,
                    }"
                    ><Price :price="product.calculatedPrice.listPrice.price"
                /></span>
                <span v-if="productIsOnSale" class="ml-4 font-semibold">
                    <Price :price="product.calculatedPrice.totalPrice" />
                </span>
            </p>
        </NuxtLink>
    </div>
</template>

<script setup lang="ts">
    const props = defineProps({
        classes: String,
        product: Object,
        showLabels: {
            type: Boolean,
            default: true,
        },
        addToHistory: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: 'regular',
        },
        showButtons: {
            type: Boolean,
            default: true,
        },
        padding: {
            type: Boolean,
            default: false,
        },
        requestId: {
            type: String,
            default: '',
        },
        isAboveFold: {
            type: Boolean,
            default: false,
        },
    });
    const emit = defineEmits(['closeSearch']);
    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;
    const { saleLabel, productIsOnSale } = useSaleLabel(props.product);
    const productIsNew = computed(() => props.product?.isNew === true);

    const imageSize = computed(() => {
        switch (props.size) {
            case 'big':
                return 'h-[250px] lg:h-[400px]';
            case 'search':
                return 'h-[250px] md:h-[450px] lg:h-[390px] 2xl:h-[280px]';
            default:
                return 'h-[250px] lg:h-[280px] l:h-[350px] 2xl:h-[475px]';
        }
    });

    const PRODUCT_IMAGE_WIDTH = 600;

    const coverImageSources = computed(() => {
        const url = props.product?.cover?.media?.url;

        return {
            avif: useShopwareImageSrcSet(url, { width: PRODUCT_IMAGE_WIDTH, format: 'avif' }),
            webp: useShopwareImageSrcSet(url, { width: PRODUCT_IMAGE_WIDTH, format: 'webp' }),
        };
    });

    const productLink = ref(
        props.product?.seoUrls?.length > 0
            ? ('/nl/' + props?.product?.seoUrls[0]?.seoPathInfo?.toLowerCase().replace(/^(\/?(nl\/)+)+/, '')).split(
                  '?',
              )[0] // remove all URL parameters
            : '#',
    );

    const isHovering = ref(false);

    const hoverImageSources = computed(() => {
        const media = props.product?.media;
        if (!Array.isArray(media)) {
            return null;
        }
        const url = media.find((item: any) => item.position === 1)?.media?.url;
        if (!url) {
            return null;
        }

        return {
            avif: useShopwareImageSrcSet(url, { width: PRODUCT_IMAGE_WIDTH, format: 'avif' }),
            webp: useShopwareImageSrcSet(url, { width: PRODUCT_IMAGE_WIDTH, format: 'webp' }),
        };
    });

    const activeImageSources = computed(() =>
        isHovering.value && hoverImageSources.value ? hoverImageSources.value : coverImageSources.value,
    );

    const handleMouseEnter = (): void => {
        if (window.innerWidth >= 1024) {
            isHovering.value = true;
        }
    };

    const handleMouseLeave = (): void => {
        isHovering.value = false;
    };

    watch(props, () => {
        productLink.value =
            '/nl/' + props?.product?.seoUrls[0]?.seoPathInfo?.toLowerCase().replace(/^(\/?(nl\/)+)+/, '');
    });

    const onProductClick = inject<((id: string) => void) | undefined>('onProductClick', undefined);

    function addToState(id: string, requestId: string): void {
        if (props.addToHistory) {
            history.pushState({ ...window.history.state, product: id }, '', productLink.value);
        }

        onProductClick?.(id);

        const { $tweakwise } = useNuxtApp();
        if (requestId) {
            $tweakwise?.trackItemClick(props?.product?.parentId, requestId);
        }
        emit('closeSearch');
    }

    onMounted(() => {
        const { product, ...updatedState } = history.state;
        if (product) {
            const productId = product;

            history.replaceState(updatedState, '');
            nextTick(() => {
                const productElement = document.querySelector(`[data-product="${productId}"]`);
                if (productElement) {
                    const rect = productElement.getBoundingClientRect();
                    window.scrollTo({
                        top: window.scrollY + rect.top - 300, // Adjust offset if needed
                        behavior: 'instant',
                    });
                }
            });
        }
    });
</script>
